import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import type { Department } from "../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { resolveEffectiveWeekStartDay } from "../MasterSchedule/v2/resolveEffectiveWeekStartDay";
import { getThisAndNextWeek } from "./utils";

type Props = {
	open: boolean;
	department: Department;
	today: DateTime;
	isSwitching: boolean;
	onSwitch: () => void;
	onKeepAsIs: () => void;
	onCancel: () => void;
};

export function WeekParityConfirmDialog({ open, department, today, isSwitching, onSwitch, onKeepAsIs, onCancel }: Props) {
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const { current, next } = getThisAndNextWeek(department, today, resolveEffectiveWeekStartDay(isRuntimeWeekStartEnabled, department.week_start_day));

	return (
		<Dialog open={open} onClose={onCancel} aria-labelledby="week-parity-dialog-title">
			<DialogTitle sx={{ color: "primary.dark" }} id="week-parity-dialog-title">
				Confirm week numbering
			</DialogTitle>
			<DialogContent>
				<Typography component="div" sx={{ mb: 2 }}>
					Currently:
				</Typography>
				<Box component="ul" sx={{ pl: 3, mt: 0, mb: 2 }}>
					<li>
						<Typography component="span">
							This week (<strong>{current.range}</strong>) is defined as <strong>Week {current.weekNumber}</strong>
						</Typography>
					</li>
					<li>
						<Typography component="span">
							Next week (<strong>{next.range}</strong>) is defined as <strong>Week {next.weekNumber}</strong>
						</Typography>
					</li>
				</Box>
				{/* Names no weekday: the switch bumps the `first_sunday` column, but for a department
				    whose week starts on Thursday the operator is looking at a Thu–Wed range above, and
				    "first Sunday" reads as a different setting entirely (DAT-832). */}
				<Typography>Do you want to switch? (This will shift the department's week numbering by one week.)</Typography>
			</DialogContent>
			<DialogActions>
				<SystemButton onClick={onCancel} buttonVariant="borderless" sx={{ color: "primary.main", fontSize: "14px" }} disabled={isSwitching}>
					Cancel
				</SystemButton>
				<SystemButton onClick={onKeepAsIs} buttonVariant="borderless" sx={{ color: "primary.main", fontSize: "14px" }} disabled={isSwitching}>
					Keep as is
				</SystemButton>
				<SystemButton onClick={onSwitch} buttonVariant="borderless" loading={isSwitching} sx={{ color: "primary.main", fontSize: "14px" }}>
					Switch
				</SystemButton>
			</DialogActions>
		</Dialog>
	);
}
